// object-card/corpse-body.ts — corpse-as-inventory step 3 (owner design 2026-09-23,
// plans/corpse-as-inventory-handoff-2026-09-23.md): the container view of a CORPSE row proxies through
// its link to the dead creature and shows what that body still wears and carries.
//
// ⛔ GEAR NEVER MOVES AT DEATH. The corpse row owns none of this — every item stays possessed by the
// dead encounter_creatures row (possess_id = the creature). Each tile's own possess_id is therefore the
// creature, so dragging one out is an ordinary move FROM the creature (move_draggable_inventory); nothing
// here re-possesses anything.
//
// Loot rule (owner 2026-09-23): taking WORN gear is a doff the LOOTER pays while the encounter is in
// combat, instant out of combat. Take All therefore sweeps
// worn gear only out of combat.
import { encounterCreatureField, inventoryField, itemsCol } from '../shared/array-types';
import { inventoryTileMount } from './render-object-card';
import type { CachedItemNode as ObjectCardBlob } from './find-cached-item';
import { nodeHasStatusItemId, INTEGRATED_OBJECT_ITEM_ID } from './find-cached-item';

type Row = Record<string, unknown>;

const host = window as Window & {
    FT_ICON_REGISTRY?: Record<string, { tooltip?: string }>;
    ftGetActiveEncounterId?: () => string;
};
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const listOf = (value: unknown): Row[] => (Array.isArray(value) ? value : [])
    .filter((entry): entry is Row => !!entry && typeof entry === 'object');

/** The dead creature this row is the body of (the corpse predicate), or '' for every ordinary row. */
export function corpseCreatureIdOf(arr: ObjectCardBlob | Row | null | undefined): string {
    const id = upper(arr ? inventoryField(arr as ObjectCardBlob, 'inventory_encounter_creature_id') : '');
    return UUID.test(id) ? id : '';
}

function activeEncounter(): Row | null {
    const id = upper(host.ftGetActiveEncounterId?.());
    if (!id) return null;
    const cache = window.FateTide?.encounterCache;
    return (cache?.get(id) ?? cache?.get(id.toLowerCase()) ?? null) as Row | null;
}

/** in_combat is an ENCOUNTER column. No loaded encounter = unknown = in combat, so the UI never promises
 *  an instant doff the server would refuse. Inside a LOADED encounter an absent value is 0: the lean
 *  encounter payload omits numeric zeroes (found by the loot smoke — "missing" read as "fighting"). */
export function corpseEncounterInCombat(): boolean {
    const enc = activeEncounter();
    if (!enc) return true;
    const encounters = (enc.encounters ?? {}) as Row;
    return Number(encounters.in_combat ?? enc.in_combat ?? 0) === 1;
}

/** Encounter truth owns the body; a standalone caster is useful only outside that held encounter. */
function creatureRecord(ecId: string): Row | null {
    for (const creature of listOf(activeEncounter()?.encounter_creatures_arr)) {
        if (upper(encounterCreatureField(creature.encounter_creatures, 'encounter_creature_id')) === ecId) return creature;
    }
    return (window.FateTide?.casterCache?.get(ecId) ?? null) as Row | null;
}

/**
 * Rows the body directly holds: live, possessed by the creature itself, not absorbed by a shapeshift.
 * Monster bits still INTEGRATED into the body are parts to harvest, not loot (the creature Inventory tab's
 * own rule, inventory.ts buildOnHandColumn): listed apart, never offered to drag or Take All.
 */
function bodyRows(ecId: string): { worn: Row[]; carried: Row[]; harvestable: Row[] } | null {
    const record = creatureRecord(ecId);
    if (!record) return null;
    // Both a live flag and an empty inventory are omitted by the lean compile. Rendering held
    // state must never fetch/recompile a creature or repaint other containers to fill those omissions.
    if (Number(encounterCreatureField(record.encounter_creatures, 'encounter_creature_dead') ?? 0) === 0)
        return { worn: [], carried: [], harvestable: [] };
    const rows = listOf(record.inventory);
    const own = rows.filter(row => upper(inventoryField(row as ObjectCardBlob, 'possess_id')) === ecId
        && Number(inventoryField(row as ObjectCardBlob, 'inventory_deleted') ?? 0) !== 1
        && Number(inventoryField(row as ObjectCardBlob, 'inventory_private') ?? 0) !== 2);
    const equipped = (row: Row) => upper(inventoryField(row as ObjectCardBlob, 'equipped'));
    // A monster bit still on a body is always integrated (the death mints it that way), and a freshly minted
    // bit can reach this cache before its integrated_object status does - so the type alone decides too.
    // (N10 2026-10-03, real death-minted body on staging: possessions carry FLAT status rows and no `items` bucket — itemsCol()'s
    // status-entry bridge reads `status_item_id`, so the status check below already classifies them; no change needed.)
    const integrated = (row: Row) => nodeHasStatusItemId(row as ObjectCardBlob, 'target', INTEGRATED_OBJECT_ITEM_ID)
        || String(itemsCol(row as ObjectCardBlob, 'item_type') ?? '') === 'monster_bit';
    // Integrated parts first - worn or not, they are harvested, never looted.
    return {
        worn: own.filter(row => !integrated(row) && equipped(row) !== ''),
        carried: own.filter(row => !integrated(row) && equipped(row) === ''),
        harvestable: own.filter(row => integrated(row)),
    };
}

/** What Take All may move off the body: carried always, worn only out of combat (instant doff). */
export function corpseTakeAllRows(arr: ObjectCardBlob | Row): Row[] {
    const ecId = corpseCreatureIdOf(arr);
    const rows = ecId ? bodyRows(ecId) : null;
    if (!rows) return [];
    return corpseEncounterInCombat() ? rows.carried : [...rows.carried, ...rows.worn];
}

function slotLabel(slotId: string): string {
    return String(host.FT_ICON_REGISTRY?.[slotId]?.tooltip ?? '').trim() || 'Worn';
}

/** WORN + CARRIED sections for a corpse's container view. '' for any row that is not a linked body. */
export function renderCorpseBodySections(arr: ObjectCardBlob): string {
    const ecId = corpseCreatureIdOf(arr);
    if (!ecId) return '';
    const rows = bodyRows(ecId);
    if (!rows) {
        return `<div class="ft-corpse-body p-2 text-muted" data-ft-corpse-body="${escapeHtml(ecId)}">Body inventory is unavailable.</div>`;
    }
    const inCombat = corpseEncounterInCombat();
    const wornTiles = rows.worn.map(row => {
        const slot = upper(inventoryField(row as ObjectCardBlob, 'equipped'));
        return `<div class="ft-corpse-body__worn d-inline-flex flex-column align-items-center m-1" data-ft-corpse-slot="${escapeHtml(slot)}">`
            + inventoryTileMount(row as ObjectCardBlob)
            + `<small class="ft-corpse-body__slot text-muted d-inline-flex align-items-center gap-1">${iconMap(slot, 14, 14)}${escapeHtml(slotLabel(slot))}</small></div>`;
    }).join('');
    const carriedTiles = rows.carried.map(row => inventoryTileMount(row as ObjectCardBlob)).join('');
    // Harvestable parts are TILES too (step 5): opening one shows its Gather control — the creature card's
    // skinning tab is out of reach now that the body, not the creature, is what players click. Dragging one
    // out is refused server-side (integrated_object) and Take All never sends them.
    const harvestTiles = rows.harvestable.map(row => inventoryTileMount(row as ObjectCardBlob)).join('');
    const section = (title: string, icon: string, note: string, tiles: string, empty: string, kind: string) => `
    <div class="mt-2 mb-0 pt-2 pb-2 px-2 bg-secondary text-light bg-gradient rounded-top">
        <h5 class="mb-0 text-left d-inline-flex align-items-center gap-2">${iconMap(icon, 22, 22)}${escapeHtml(title)}</h5>
        ${note ? `<small class="d-block">${escapeHtml(note)}</small>` : ''}
    </div>
    <div class="d-flex justify-content-center flex-wrap border rounded-bottom p-2" data-ft-corpse-section="${kind}">
        ${tiles || `<div class="mt-2 mb-1 text-muted">${escapeHtml(empty)}</div>`}
    </div>`;
    return `<div class="ft-corpse-body" data-ft-corpse-body="${escapeHtml(ecId)}">`
        + section('Worn', 'armor', inCombat ? 'In combat, taking worn gear means doffing it — you pay the time.' : 'Out of combat, worn gear comes off at once.',
            wornTiles, 'Wearing nothing.', 'worn')
        + section('Carried', 'container', '', carriedTiles, 'Carrying nothing.', 'carried')
        + (harvestTiles ? section('Harvestable', 'monster_bit', 'Parts of the body — open one and gather it to take it.', harvestTiles, '', 'harvestable') : '')
        + '</div>';
}
